import { LBtn, LModal, LTableResults } from "@components";
import { convertToSentenceCase } from "@helpers/stringHelper";
import { useApi } from "@services/useApi";
import { WordRequest } from "@store/generalTypes";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

export const WordSuggestions = (): JSX.Element => {
    const validateMethods = useForm();
    const [data, setData] = useState<WordRequest[]>([]);
    const [showConfirm, setShowConfirm] = useState(false);
    const [wordToAdd, setWordToAdd] = useState<[string, string]>();

    const { getWordRequests, setAlternateForWord, loading } = useApi();

    const handleGetWordRequests = async (): Promise<void> => {
        const wordRequests = await getWordRequests();
        setData(wordRequests.items);
    };

    const handleOpenModal = (word: string, alternate: string): void => {
        setWordToAdd([word, alternate.toLocaleLowerCase()]);
        setShowConfirm(true);
    };

    const handleCloseModal = (): void => {
        setShowConfirm(false);
    };

    const handleOnConfirm = async (): Promise<void> => {
        await setAlternateForWord(wordToAdd![0], wordToAdd![1]!);
        setData([]);
        void handleGetWordRequests();
        handleCloseModal();
    };

    useEffect(() => {
        void handleGetWordRequests();
    }, []);

    const columnHelper = createColumnHelper<WordRequest>();

    const baseColumns: Array<ColumnDef<WordRequest, any>> = [
        columnHelper.accessor("targetWord", {
            header: () => "Expected Word",
            cell: ({ row }) => convertToSentenceCase(row.original.targetWord)
        }),
        columnHelper.accessor("response", {
            header: () => "Recorded Word",
            cell: ({ row }) => convertToSentenceCase(row.original.response)
        }),
        columnHelper.accessor("count", {
            header: () => "How Many Times Heard",
            cell: ({ row }) => row.original.count
        }),
        {
            id: "menu",
            enableSorting: false,
            header: () => "Add to Alternate",
            cell: ({ row }) => {
                return (
                    <LBtn
                        noSpacing
                        isBold
                        onClick={() => {
                            handleOpenModal(row.original.targetWord, row.original.response);
                        }}
                    >
                        Add
                    </LBtn>
                );
            }
        }
    ];

    return (
        <>
            <FormProvider {...validateMethods}>
                {LTableResults<WordRequest>(data, baseColumns, {
                    isRoundedHeader: true,
                    showStriping: true
                })}
            </FormProvider>
            {showConfirm && (
                <LModal
                    onClose={handleCloseModal}
                    onConfirm={handleOnConfirm}
                    confirmLoading={loading}
                    hasCancelConfirmButtons
                    width="s"
                    fullHeight={false}
                    title={<p className="has-text-weight-bold is-size-4 mx-5 has-text-centered">Are you sure?</p>}
                >
                    <div className="is-flex is-flex-direction-column">
                        <p className="content has-text-centered is-size-5 mt-4">
                            This will add the response as a valid alternate to the expected word
                        </p>
                    </div>
                </LModal>
            )}
        </>
    );
};
